<template>
  <div class="wrapper">
    <div id="create">
      <el-card class="no-padding">
        <h3>Deploy on Cloud</h3>

        <el-collapse v-model="active">
          <el-collapse-item :title="title" name="application">
            <div
              v-if="step > 1 && bundle"
              slot="title"
              :class="{ [form.bundleId]: true, app: true }"
            >
              {{ bundle.name }}
              <span v-if="form.pkgVersion">({{ form.pkgVersion }})</span>
              <span v-else>({{ form.version }})</span>
            </div>

            <Applications
              :bundle-id.sync="form.bundleId"
              :version.sync="form.version"
              :pkg-version.sync="form.pkgVersion"
            />

            <el-button
              type="primary"
              size="small"
              :disabled="!form.bundleId"
              @click.prevent="next(2, 'location')"
            >
              Continue
            </el-button>

            <el-button
              v-if="!$route.params.type"
              type="text"
              class="skip"
              @click.prevent="skip"
            >
              Skip this step
            </el-button>
          </el-collapse-item>

          <el-collapse-item v-if="step > 1" title="Choose a location" name="location">
            <div
              v-if="step > 2 && country"
              slot="title"
              class="location"
              :style="{ backgroundImage: 'url('+ country.flag +')' }"
            >
              {{ country.name }}
              <span v-if="plan">({{ plan.region.name }})</span>
            </div>

            <Locations :country.sync="country" @change="next(3, 'plan', country !== null)" />
          </el-collapse-item>

          <el-collapse-item v-if="step > 2" title="Choose a plan" name="plan">
            <div
              v-if="step > 3 && plan"
              slot="title"
              class="app"
              :style="{ backgroundImage: 'url(' + planProviderLogo(plan.id) + ')' }"
            >
              {{ planDescription(plan.id) }} ({{ plan.name }})
            </div>

            <Plans :plan-id.sync="form.planId" :country="country" />
          </el-collapse-item>

          <el-collapse-item v-if="step > 3 && plan" title="Finalize your server" name="summary">
            <el-form>
              <el-form-item
                label="Give your server an identifying name you will remember them by."
                :error="nameError"
              >
                <el-input
                  v-model="form.name"
                  placeholder="Enter Server name"
                  autofocus
                />
              </el-form-item>

              <el-alert
                v-if="plan.sandbox"
                type="warning"
                title="You have selected a sandbox Cloud Server"
                description="You will not be able to upgrade the resources or increase the size of the disk."
                effect="dark"
                :closable="false"
                show-icon
              />

              <el-form-item
                label="Choose disk size for application data :"
                :error="storageError"
              >
                <el-slider
                  v-model="form.storage.size"
                  :min="10"
                  :max="100"
                  :step="5"
                  :marks="{ 10: '10 GB', 30: '30 GB', 50: '50 GB', 75: '75 GB' }"
                  show-input
                  :disabled="!!plan.sandbox"
                />
              </el-form-item>

              <Billing
                :reserved.sync="form.reserved"
                :price="plan.price"
                :storage-size="form.storage.size"
              />

              <span class="notice">
                The monthly rate will be charged as soon as you launch a Cloud Server. Save ~30% by opting for monthly billing.
              </span>

              <el-alert
                v-if="plan.provider.name === 'OVH'"
                type="error"
                title="With OVH the choice of monthly rate is definitive, you cannot change it later."
                :closable="false"
              />

              <el-button
                type="primary"
                size="large"
                :loading="loading"
                @click.prevent="create"
              >
                Create Cloud Server
              </el-button>
            </el-form>
          </el-collapse-item>
        </el-collapse>
      </el-card>
    </div>

    <el-dialog
      :visible.sync="payment"
      title="Choose a payment method"
      top="5vh"
      width="600px"
      append-to-body
    >
      <BillingStripeCard @save="updatePayment" @cancel="payment = false" />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Applications from '../components/Create/Applications'
import Locations from '../components/Create/Locations'
import Plans from '../components/Create/Plans'
import Billing from '../components/Instance/Billing'
import BillingStripeCard from '../components/Billing/StripeCard'

export default {
  components: {
    Applications,
    Locations,
    Plans,
    Billing,
    BillingStripeCard
  },
  data () {
    return {
      active: ['application', 'location', 'plan', 'storage'],
      defaultSort: {
        prop: 'created',
        order: 'descending'
      },
      step: 1,
      country: null,
      nameError: null,
      storageError: null,
      payment: false,
      loading: false,
      form: {
        bundleId: this.$route.params.type,
        version: null,
        pkgVersion: null,
        planId: null,
        name: null,
        reserved: false,
        storage: {
          size: 10
        }
      }
    }
  },
  mounted () {
    window.scrollTo(0, 0)
  },
  computed: {
    title () {
      return this.$route.params.type ? this.bundle.name + ' configuration' : 'Choose an application'
    },
    bundle () {
      return this.form.bundleId ? this.$root.bundles[this.form.bundleId] : null
    },
    plan () {
      return this.plans.find((p) => p.id === this.form.planId)
    },
    ...mapState(['instances', 'plans']),
    ...mapGetters(['permission'])
  },
  methods: {
    next (step, active, test) {
      if (test !== undefined && !test) {
        return
      }
      this.step = step
      this.active = [active]
    },
    skip () {
      this.form.bundleId = null
      this.next(2, 'location')
    },
    create () {
      this.nameError = this.storageError = null

      if (!this.form.name) {
        this.nameError = 'Please give an identifying name'
        return
      }
      if (isNaN(this.form.storage.size) || (this.form.storage.size % 5) !== 0 ||
        this.form.storage.ize < 10 || this.form.storage.size > 100) {
        this.storageError = 'Disk size must be a multiple of 5 (min: 10, max: 100)'
        return
      }

      if (this.permission('owner') || this.permission('admin')) {
        this.$store.dispatch('getBillingCustomer').then((customer) => {
          if (!customer.invoice_settings.default_payment_method) {
            this.payment = true
            return
          }

          this.loading = true
          return this.$store.dispatch('createInstance', this.form)
            .then(() => this.$router.push('/instances'))
        }).catch(this.failed)
      } else {
        this.loading = true
        return this.$store.dispatch('createInstance', this.form)
          .then(() => this.$router.push('/instances'))
          .catch(this.failed)
      }
    },
    failed (err) {
      this.loading = false
      if (err.toString().match(/429/)) {
        this.$alert('You have recently created a server, please wait an hour before creating a new one.', 'Error', {
          confirmButtonText: 'OK'
        })
      }
    },
    updatePayment () {
      this.create()
      this.payment = false
    }
  },
  watch: {
    '$route.params.type' (value) {
      this.form.pkgVersion = null
      this.form.bundleId = value
    },
    'form.planId' () {
      this.next(4, 'summary')
      setTimeout(() => document.getElementById('app-main').scrollTo(0, document.body.scrollHeight), 150)
    },
    country () {
      this.form.planId = null
    }
  }
}
</script>

<style lang="scss">
  @import "../theme";

  #create {
    .el-collapse-item__header, .el-collapse-item__content {
      padding: 0 20px;
    }
    .el-collapse-item__header {
      .app, .location {
        background-position: center left;
        background-repeat: no-repeat;
        background-size: 25px;
        padding-left: 35px;
      }
    }
    .el-collapse-item__wrap {
      border-bottom: none;
    }

    .choose-app {
      margin-bottom: 20px;

      .el-button--text {
        color: $--color-text-primary;
      }
    }
    .bundle-config {
      .el-card.is-always-shadow, .el-card.is-hover-shadow:hover {
        box-shadow: $--card-box-shadow-small;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import "../theme";

  .wrapper {
    background-color: $--background-color-base;
    padding-top: 0;

    #create {
      margin-left: auto !important;
      margin-right: auto !important;
      max-width: 800px;
      padding-top: 40px;
    }
  }

  h3 {
    margin: 20px;
  }

  .el-collapse {
    margin-bottom: 20px;
  }

  .el-slider {
    margin: 30px 0 30px 10px;
  }

  .el-form {
    .notice {
      color: #777;
      font-size: 11px;
    }

    .el-alert {
      margin-top: 10px;

      &.el-alert--info {
        background-color: $--free-trial-color;
      }
    }
  }

  .el-button--primary.el-button--large {
    display: block;
    width: 100%;
    max-width: 400px;
    margin: 30px auto 50px;
    font-size: 18px;
  }

  .skip {
    float: right;
  }
</style>
